import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { activitiesApi, notificationsApi, skillsApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import SkillCard from '../components/SkillCard';
import { CategoryModal } from '../components/CategoryModal';
import ConfirmModal from '../components/ConfirmModal';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, Star, Eye, TrendingUp, Plus, Sparkles, Zap, Users, Download } from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [mySkills, setMySkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [skillToEditCategory, setSkillToEditCategory] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [activitiesRes, notificationsRes, skillsRes] = await Promise.all([
        activitiesApi.list().catch(() => ({ data: [] })),
        notificationsApi.list().catch(() => ({ data: [] })),
        skillsApi.list({ user_id: user.id }).catch(() => ({ data: [] })),
      ]);
      setActivities(activitiesRes.data);
      setNotifications(notificationsRes.data);
      setMySkills(skillsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const handleEditSkill = (skill) => {
    navigate(`/skills/${skill.id}/edit`);
  };

  const handleDeleteSkill = (skill) => {
    setSkillToDelete(skill);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!skillToDelete) return;
    try {
      await skillsApi.delete(skillToDelete.id);
      setMySkills(mySkills.filter(s => s.id !== skillToDelete.id));
      toast.success('Skill deleted successfully');
      setDeleteModalOpen(false);
      setSkillToDelete(null);
    } catch (error) {
      console.error('Failed to delete skill', error);
      toast.error('Failed to delete skill');
    }
  };

  const handleCategoryChange = (skill) => {
    setSkillToEditCategory(skill);
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = async (categoryId) => {
    if (!skillToEditCategory) return;
    try {
      await skillsApi.update(skillToEditCategory.id, { category_id: categoryId });
      setMySkills(mySkills.map(s =>
        s.id === skillToEditCategory.id ? { ...s, category_id: categoryId } : s
      ));
      toast.success('Category updated');
      setCategoryModalOpen(false);
      setSkillToEditCategory(null);
    } catch (error) {
      console.error('Failed to update category', error);
      toast.error('Failed to update category');
    }
  };

  const handleUpdateVisibility = async (skillId, visibility) => {
    try {
      await skillsApi.update(skillId, { visibility });
      setMySkills(mySkills.map(s =>
        s.id === skillId ? { ...s, visibility } : s
      ));
      toast.success(`Visibility updated to ${visibility}`);
    } catch (error) {
      console.error('Failed to update visibility', error);
      toast.error('Failed to update visibility');
    }
  };

  const totalStats = mySkills.reduce(
    (acc, skill) => ({
      stars: acc.stars + (skill.star_count || 0),
      downloads: acc.downloads + (skill.install_count || 0),
    }),
    { stars: 0, downloads: 0 }
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2" data-testid="dashboard-title">
            Welcome back, {(user?.display_name || user?.username || '').split(' ')[0]}! 👋
          </h1>
          <p className="text-lg text-text-gray dark:text-dark-text-secondary">
            Here's what's happening with your skills today
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => navigate('/browse')} 
            variant="outline"
            className="px-6 py-3 rounded-xl font-semibold border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all"
            data-testid="browse-skills-button"
          >
            Browse Skills
          </Button>
          <Button 
            onClick={() => navigate('/builder')} 
            className="gradient-primary text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
            data-testid="create-skill-button"
          >
            <Plus className="mr-2 h-5 w-5" />
            Create Skill
          </Button>
        </div>
      </div>

      {/* Stats Grid - 3 Metrics Only */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl">
              <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1" data-testid="total-skills-stat">
            {mySkills.length}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Skills
          </div>
        </GlassCard>

        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-xl">
              <Star className="h-6 w-6 text-primary" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1" data-testid="total-stars-stat">
            {totalStats.stars}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Stars
          </div>
        </GlassCard>

        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl">
              <Download className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1" data-testid="total-downloads-stat">
            {totalStats.downloads}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Downloads
          </div>
        </GlassCard>
      </div>

      {/* Skill Idea Assistant Prompt - Prominent CTA */}
      <GlassCard variant="strong" className="overflow-hidden">
        <div className="relative">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-primary/10 to-purple-500/10 dark:from-orange-500/5 dark:via-primary/5 dark:to-purple-500/5"></div>
          
          {/* Content */}
          <div className="relative p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Icon/Visual */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {/* Animated glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-primary blur-2xl opacity-30 animate-pulse"></div>
                  <div className="relative p-6 bg-gradient-to-br from-orange-500/20 to-primary/20 rounded-2xl backdrop-blur-sm border border-orange-500/20">
                    <Zap className="h-16 w-16 text-orange-500 dark:text-orange-400" />
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-manrope font-bold text-text-dark dark:text-dark-text mb-3">
                  Not sure what skill to build?
                </h2>
                <p className="text-lg text-text-gray dark:text-dark-text-secondary mb-6 max-w-2xl">
                  Our AI-powered <span className="font-semibold text-primary">Skill Idea Assistant</span> will give you immediate clarity and help you discover your next Claude Skill through an interactive conversation.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Button
                    onClick={() => navigate('/discover')}
                    className="gradient-primary text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all text-base"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Start Skill Discovery
                  </Button>
                  <Button
                    onClick={() => navigate('/builder')}
                    variant="outline"
                    className="px-8 py-4 rounded-xl font-semibold border-2 text-base"
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    Skip to Builder
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Removed Recent Activity and Notifications sections */}
      <div className="hidden md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <GlassCard variant="strong" className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-lg">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-manrope font-bold text-lg text-text-dark dark:text-dark-text">
                Recent Activity
              </h3>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Activity from people you follow
              </p>
            </div>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-200 dark:bg-dark-border rounded-xl skeleton"></div>
              ))}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8" data-testid="no-activities-message">
              <Users className="h-12 w-12 mx-auto mb-3 text-text-light dark:text-dark-text-secondary opacity-50" />
              <p className="text-text-gray dark:text-dark-text-secondary">No recent activity</p>
              <p className="text-sm text-text-light dark:text-dark-text-secondary mt-1">
                Follow creators to see their activity here
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="activities-list">
              {activities.slice(0, 5).map((activity) => (
                <div 
                  key={activity.user?.id} 
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-border/30 transition-colors"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={activity.user?.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary-dark/20 text-primary">
                      {activity.user?.username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-sm">
                    <span className="font-semibold text-text-dark dark:text-dark-text">
                      {activity.user?.username}
                    </span>
                    <span className="text-text-gray dark:text-dark-text-secondary">
                      {activity.type === 'skill_published' && ' published a new skill'}
                      {activity.type === 'follow' && ' followed someone'}
                      {activity.type === 'star' && ' starred a skill'}
                    </span>
                    {activity.skill && (
                      <Link 
                        to={`/skills/${activity.skill.id}`} 
                        className="block text-primary hover:underline mt-1 font-medium"
                      >
                        {activity.skill.name}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        {/* Notifications */}
        <GlassCard variant="strong" className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg">
              <Bell className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-manrope font-bold text-lg text-text-dark dark:text-dark-text">
                Notifications
              </h3>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Recent notifications
              </p>
            </div>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-200 dark:bg-dark-border rounded-xl skeleton"></div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8" data-testid="no-notifications-message">
              <Bell className="h-12 w-12 mx-auto mb-3 text-text-light dark:text-dark-text-secondary opacity-50" />
              <p className="text-text-gray dark:text-dark-text-secondary">No notifications</p>
              <p className="text-sm text-text-light dark:text-dark-text-secondary mt-1">
                We'll notify you when something happens
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="notifications-list">
              {notifications.slice(0, 5).map((notification) => (
                <div
                  key={notification.id}
                  className={`
                    flex items-start gap-3 p-3 rounded-xl cursor-pointer
                    ${!notification.is_read 
                      ? 'bg-primary/10 dark:bg-primary/20' 
                      : 'hover:bg-gray-50 dark:hover:bg-dark-border/30'
                    }
                    transition-colors
                  `}
                  onClick={() => markNotificationAsRead(notification.id)}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={notification.from_user?.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary-dark/20 text-primary">
                      {notification.from_user?.username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-sm">
                    <p className="text-text-dark dark:text-dark-text">{notification.message}</p>
                    {!notification.is_read && (
                      <Badge className="mt-1 text-xs bg-primary">New</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      {/* My Skills */}
      <GlassCard variant="strong" className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-manrope font-bold text-2xl text-text-dark dark:text-dark-text">
              My Skills
            </h3>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary">
              Manage your created skills
            </p>
          </div>
          {mySkills.length > 0 && (
            <Button 
              onClick={() => navigate('/builder')} 
              variant="outline"
              className="rounded-xl"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Skill
            </Button>
          )}
        </div>

        {mySkills.length === 0 ? (
          <div className="text-center py-16" data-testid="no-skills-message">
            <div className="inline-block p-6 bg-gradient-to-br from-primary/10 to-primary-dark/10 rounded-2xl mb-4">
              <Sparkles className="h-16 w-16 text-primary mx-auto" />
            </div>
            <h4 className="text-xl font-bold text-text-dark dark:text-dark-text mb-2">
              No skills yet
            </h4>
            <p className="text-text-gray dark:text-dark-text-secondary mb-6 max-w-md mx-auto">
              Start building your first Claude Skill with our visual editor
            </p>
            <Button 
              onClick={() => navigate('/builder')} 
              className="gradient-primary text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl"
            >
              <Plus className="mr-2 h-5 w-5" />
              Create Your First Skill
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4" data-testid="my-skills-grid">
            {mySkills.map((skill) => (
              <SkillCard
                key={skill.id}
                skill={skill}
                showActions={true}
                showSlug={false}
                showStatus={true}
                onEdit={handleEditSkill}
                onDelete={handleDeleteSkill}
                onCategoryChange={handleCategoryChange}
                onVisibilityChange={handleUpdateVisibility}
              />
            ))}
          </div>
        )}
      </GlassCard>

      {/* Category Modal */}
      {categoryModalOpen && (
        <CategoryModal
          isOpen={categoryModalOpen}
          onClose={() => {
            setCategoryModalOpen(false);
            setSkillToEditCategory(null);
          }}
          onSave={handleSaveCategory}
          currentCategory={skillToEditCategory?.category || skillToEditCategory?.category_id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setSkillToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Skill"
          message={`Are you sure you want to delete "${skillToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmVariant="destructive"
        />
      )}
    </div>
  );
};

export default DashboardPage;
